import settings from '@solinkcorp/settings';
import { type ExternalVms, type OrchidCamera, type OrchidExternalVms } from './externalVms';
import { sanitizeUrl } from './urls';

interface OrchidFusionParams {
  baseUrl: string;
  orchidId: string;
  streamId: number;
  startTime: number | undefined;
}

export function buildSinglePlayerUrl(params: OrchidFusionParams): string {
  const url = new URL(params.baseUrl);
  url.searchParams.set('single-player', String(params.streamId));
  url.searchParams.set('single-player-orchid', params.orchidId);
  if (params.startTime != null) {
    url.searchParams.set('start-time', String(params.startTime));
  }
  url.searchParams.set('hide-smart-search', 'true');
  url.searchParams.set('hide-thumbnail-search', 'true');
  url.searchParams.set('hide-ai-search', 'true');
  url.searchParams.set('show-header', 'true');
  url.searchParams.set('disable-webrtc', 'true');
  return url.toString();
}

const ORCHID_API_BASE_URL = settings.callhome_url;

interface OrchidAuthResponse {
  sessionId: string;
  token: string;
}

async function fetchOrchidAuth(accessToken: string): Promise<OrchidAuthResponse> {
  const response = await fetch(`${ORCHID_API_BASE_URL}/api/users/authorize/orchid`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Orchid auth: ${response.status}`);
  }

  return response.json();
}

export async function openOrchidCamera(
  vms: OrchidExternalVms,
  camera: OrchidCamera,
  accessToken: string,
): Promise<void> {
  const safeBaseUrl = sanitizeUrl(vms.baseUrl);
  if (!safeBaseUrl) {
    console.error('[OrchidPlayer] invalid baseUrl');
    return;
  }

  const singlePlayerUrl = buildSinglePlayerUrl({
    baseUrl: safeBaseUrl,
    orchidId: vms.orchidId,
    streamId: camera.streamId,
    startTime: vms.timestamp,
  });

  const hostedPageUrl = new URL('/orchid-player.html', window.location.origin);
  hostedPageUrl.searchParams.set('playerUrl', singlePlayerUrl);
  hostedPageUrl.searchParams.set('playerOrigin', new URL(safeBaseUrl).origin);
  const safeHybridLink = sanitizeUrl(vms.hybridLink);
  if (safeHybridLink) {
    hostedPageUrl.searchParams.set('hybridLink', safeHybridLink);
  }

  const newTab = window.open(hostedPageUrl.toString(), '_blank');
  if (!newTab) {
    console.error('[OrchidPlayer] popup blocked');
    return;
  }

  const sendAuth = async () => {
    try {
      const { sessionId, token } = await fetchOrchidAuth(accessToken);
      newTab.postMessage({ type: 'orchid-session', sessionId, token }, window.location.origin);
    } catch (error) {
      console.error('[OrchidPlayer] auth fetch failed:', error);
      newTab.postMessage({ type: 'orchid-session-error' }, window.location.origin);
    }
  };

  if (newTab.document.readyState === 'complete') {
    sendAuth();
  } else {
    newTab.addEventListener('load', sendAuth, { once: true });
  }
}

export async function openExternalVms(
  vms: ExternalVms,
  accessToken: string,
  camera?: OrchidCamera,
): Promise<void> {
  switch (vms.vendor) {
    case 'orchid': {
      if (camera) {
        return openOrchidCamera(vms, camera, accessToken);
      }
      if (vms.cameras.length > 0) {
        return openOrchidCamera(vms, vms.cameras[0], accessToken);
      }
      break;
    }
  }
}
